import { ConditionSchema } from 'features/libra/sections/formLayout'
import { z } from 'zod'

/**
 * One signature block. All of its copy is config-authored; `roles` carries the
 * same permission-string semantics as a lifecycle action trigger's `roles`.
 */
export const signoffSlotSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** Compact label for tight spaces such as dashboard pills (e.g. "SME"); `title` when absent. */
  shortTitle: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  dateLabel: z.string().default('Signed'),
  roles: z.array(z.string()).default([]),
  /**
   * When a gated transition needs this slot signed. Empty (the default) means
   * always. Phrase a waiver negatively (`!= 'ineligible'` rather than
   * `is_truthy`) so an unrecorded value leaves the slot required.
   */
  requiredWhen: z.array(ConditionSchema).default([]),
})

export const signoffConfigSchema = z.object({
  // Duplicate ids would key the same stored slot from two cards.
  signoffs: z
    .array(signoffSlotSchema)
    .min(1)
    .refine((slots) => new Set(slots.map((s) => s.id)).size === slots.length, {
      message: 'Sign-off slot ids must be unique',
    }),
  /**
   * Lifecycle stage ids in which signing is enabled. Required because stage
   * ids are per-workflow: a default would silently lock the panel elsewhere.
   */
  enabledStages: z.array(z.string()).min(1),
})

export const signoffRecordSchema = z.object({
  userId: z.string(),
  userName: z.string(),
  signedAt: z.string(),
})

/**
 * One slot's state: `signatures` is append-only and oldest first, `active`
 * says whether the last one still counts. A reset flips only the flag, so it
 * cannot damage the history.
 *
 * Read validity through `getActiveSignature`, never off `active` — a flag over
 * an empty list is representable and means nothing.
 */
export const signoffSlotStateSchema = z.object({
  signatures: z.array(signoffRecordSchema).default([]),
  active: z.boolean().default(false),
})

/**
 * Every slot's state, keyed by slot id — the `signoffs` of the ticket's
 * sign-off entry.
 *
 * Keep the outer value an OBJECT. Rolodex patches are a sparse deep diff (see
 * `createPatchData`), so per-slot paths merge and two reviewers signing
 * different slots do not clobber one another; an array would diff as a single
 * value and the second writer would replace the first wholesale.
 */
export const signoffsFieldSchema = z.record(z.string(), signoffSlotStateSchema)

export type SignoffSlot = z.infer<typeof signoffSlotSchema>
export type SignoffConfig = z.infer<typeof signoffConfigSchema>
export type SignoffRecord = z.infer<typeof signoffRecordSchema>
export type SignoffSlotState = z.infer<typeof signoffSlotStateSchema>
export type SignoffsField = z.infer<typeof signoffsFieldSchema>

/**
 * Safely parses `globalContext.signoff`. Mirrors `parseLifecycleConfig`: the
 * panel and the lifecycle gate both read the result, so the slot list has one
 * home rather than one per hosting step.
 */
export function parseSignoffConfig(
  globalContext: Record<string, unknown>
): SignoffConfig | undefined {
  const raw = globalContext.signoff
  if (!raw || typeof raw !== 'object') return undefined
  const result = signoffConfigSchema.safeParse(raw)
  if (!result.success) {
    console.warn(
      '[Libra] signoff config failed validation:',
      result.error.issues
    )
    return undefined
  }
  return result.data
}

export const EMPTY_SLOT_STATE: SignoffSlotState = {
  signatures: [],
  active: false,
}

/** `data` of the ticket's own `libra-ticket-signoff` entry. */
export const signoffEntryDataSchema = z.object({
  signoffs: signoffsFieldSchema.default({}),
})

/**
 * Fixed per ticket so there is only ever one sign-off entry, and a PATCH can
 * create it on first write without a create-or-update branch.
 */
export const getSignoffEntryId = (ticketId: string): string =>
  `signoff-${ticketId}`

/** Entry `data` that fails the shape reads as no slots signed, never as no panel. */
export const parseSignoffEntry = (data: unknown): SignoffsField => {
  const parsed = signoffEntryDataSchema.safeParse(data)
  return parsed.success ? parsed.data.signoffs : {}
}

/**
 * The slot's live signature, or undefined — which is also the answer to "is
 * this slot signed". The only correct reader of `active`.
 */
export const getActiveSignature = (
  state: SignoffSlotState
): SignoffRecord | undefined =>
  state.active ? state.signatures.at(-1) : undefined
